<template>
  <Teleport to="body">
    <Transition name="editor-subd-fade">
      <div v-if="modelValue" class="editor-subd-mask" @click.self="onClose">
        <div class="editor-subd-wrap" @click.self="onClose">
          <Transition name="editor-subd-pop">
            <dialog
              v-if="modelValue"
              ref="dialogRef"
              class="editor-subd-panel"
              open
              aria-modal="true"
              :aria-labelledby="titleId"
            >
              <header class="editor-subd-head">
                <h3 :id="titleId" class="editor-subd-title">{{ copy.title }}</h3>
                <button type="button" class="editor-subd-close" :aria-label="copy.close" @click="onClose">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" />
                  </svg>
                </button>
              </header>

              <div class="editor-subd-body">
                <section class="editor-subd-card">
                  <div class="editor-subd-card__head">
                    <h4 class="editor-subd-card__title">{{ copy.subscriptionSectionTitle }}</h4>
                    <span v-if="isLifetime" class="editor-subd-chip">{{ copy.permanent }}</span>
                    <span
                      v-else-if="showSubPlaceholder"
                      class="editor-subd-chip editor-subd-chip--warn"
                      :title="copy.queryUnavailable"
                    >
                      {{ copy.queryUnavailable }}
                    </span>
                    <span v-else-if="subStatusText" class="editor-subd-chip">{{ subStatusText }}</span>
                  </div>

                  <div class="editor-subd-kv">
                    <div class="editor-subd-kv__item">
                      <div class="editor-subd-kv__k">{{ copy.currentTier }}</div>
                      <div class="editor-subd-kv__v">{{ tierLabel }}</div>
                    </div>
                    <div v-if="showExpires" class="editor-subd-kv__item">
                      <div class="editor-subd-kv__k">{{ copy.expiresLabel }}</div>
                      <div class="editor-subd-kv__v">{{ expiresText }}</div>
                    </div>
                    <div
                      v-if="!isLifetime && sub?.current_period_start && sub?.current_period_end"
                      class="editor-subd-kv__item"
                    >
                      <div class="editor-subd-kv__k">{{ copy.currentPeriod }}</div>
                      <div class="editor-subd-kv__v">
                        {{ formatDate(sub.current_period_start) }} – {{ formatDate(sub.current_period_end) }}
                      </div>
                    </div>
                    <div v-if="showNextBilling" class="editor-subd-kv__item">
                      <div class="editor-subd-kv__k">{{ copy.nextBilling }}</div>
                      <div class="editor-subd-kv__v">{{ formatDate((sub?.next_billing_at || 0) as number) }}</div>
                    </div>
                    <div v-if="!isLifetime && sub?.cancel_at_period_end" class="editor-subd-kv__item">
                      <div class="editor-subd-kv__k">{{ copy.renewal }}</div>
                      <div class="editor-subd-kv__v">{{ copy.cancelAtPeriodEnd }}</div>
                    </div>
                  </div>

                  <p v-if="showSubPlaceholder" class="editor-subd-placeholder-note">
                    {{ copy.subStatusUnknown }}
                  </p>

                  <div class="editor-subd-actions">
                    <button
                      v-if="showSubPlaceholder"
                      type="button"
                      class="editor-subd-ghost"
                      :disabled="loadingSub"
                      @click="onRetryLoadSubscription"
                    >
                      {{ copy.refresh }}
                    </button>
                    <button
                      v-if="!isLifetime && subscriptionNo && canCancelSubscription"
                      type="button"
                      class="editor-subd-danger editor-subd-danger--soft"
                      :disabled="canceling"
                      @click="confirmOpen = true"
                    >
                      {{ canceling ? copy.cancelling : copy.cancelSubscription }}
                    </button>
                  </div>
                </section>

                <section class="editor-subd-card">
                  <div class="editor-subd-card__head">
                    <h4 class="editor-subd-card__title">{{ copy.ordersSectionTitle }}</h4>
                    <span class="editor-subd-muted">{{ copy.totalTpl(total) }}</span>
                  </div>

                  <div v-if="loadingOrders" class="editor-subd-loading">
                    <div class="editor-subd-spinner" aria-hidden="true" />
                    <div class="editor-subd-muted">{{ copy.loading }}</div>
                  </div>

                  <ul v-else class="editor-subd-orders">
                    <li v-for="o in orders" :key="o.biz_order_no" class="editor-subd-order">
                      <div class="editor-subd-order__main">
                        <div class="editor-subd-order__subject">
                          {{ orderTypeLabel(o.order_type) }}
                          <span v-if="o.plan_code" class="editor-subd-order__plan">· {{ o.plan_code }}</span>
                        </div>

                        <div class="editor-subd-order__grid">
                          <div class="editor-subd-order__kv">
                            <span class="editor-subd-order__k">{{ copy.orderNoLabel }}</span>
                            <span class="editor-subd-order__v editor-subd-order__mono">{{ o.biz_order_no }}</span>
                          </div>
                          <div class="editor-subd-order__kv">
                            <span class="editor-subd-order__k">{{ copy.payChannelLabel }}</span>
                            <span class="editor-subd-order__v">{{ channelLabel(o.pay_order?.channel) }}</span>
                          </div>
                          <div class="editor-subd-order__kv">
                            <span class="editor-subd-order__k">{{ copy.payTimeLabel }}</span>
                            <span class="editor-subd-order__v">
                              {{ formatDateTimeSeconds((o.paid_at || o.pay_order?.paid_at || o.created_at) as number) }}
                            </span>
                          </div>
                          <div class="editor-subd-order__kv">
                            <span class="editor-subd-order__k">{{ copy.productLabel }}</span>
                            <span class="editor-subd-order__v">{{ orderTypeLabel(o.order_type) }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="editor-subd-order__amount">{{ formatMoney(o.amount_minor, o.currency) }}</div>
                    </li>
                    <li v-if="orders.length === 0" class="editor-subd-empty">
                      {{ copy.emptyOrders }}
                    </li>
                  </ul>

                  <nav class="editor-subd-pager" :aria-label="copy.pagerAria">
                    <button
                      type="button"
                      class="editor-subd-pagebtn"
                      :disabled="page <= 1 || loadingOrders"
                      :aria-label="copy.prevPage"
                      @click="goPage(page - 1)"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.8" />
                      </svg>
                    </button>

                    <button
                      v-for="p in pageItems"
                      :key="String(p)"
                      type="button"
                      class="editor-subd-page"
                      :class="p === page ? 'is-active' : ''"
                      :disabled="p === '...' || loadingOrders"
                      @click="typeof p === 'number' ? goPage(p) : undefined"
                    >
                      {{ p }}
                    </button>

                    <button
                      type="button"
                      class="editor-subd-pagebtn"
                      :disabled="page >= pageCount || loadingOrders"
                      :aria-label="copy.nextPage"
                      @click="goPage(page + 1)"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.8" />
                      </svg>
                    </button>
                  </nav>
                </section>
              </div>
            </dialog>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <Transition name="editor-subd-fade">
      <div v-if="confirmOpen" class="editor-subd-confirm-mask" @click.self="confirmOpen = false">
        <div class="editor-subd-confirm">
          <h4 class="editor-subd-confirm__title">{{ copy.cancelConfirmTitle }}</h4>
          <p class="editor-subd-confirm__body">{{ copy.cancelConfirmBody }}</p>
          <div class="editor-subd-confirm__actions">
            <button
              type="button"
              class="editor-subd-confirm-ok"
              :disabled="canceling"
              @click="onCancelAtPeriodEnd"
            >
              {{ canceling ? copy.cancelling : copy.confirm }}
            </button>
            <button
              type="button"
              class="editor-subd-confirm-cancel"
              :disabled="canceling"
              @click="confirmOpen = false"
            >
              {{ copy.cancel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMockupEditorStore } from '@/stores/mockupEditor'
import { useSubscriptionDetailApi } from '../../../../shared/composables/useSubscriptionDetailApi'
import type { MarketingProxyJsonErrorCopy } from '../../../../shared/utils/marketingProxyJsonResponse'
import { readJsonFromMarketingProxyResponse } from '../../../../shared/utils/marketingProxyJsonResponse'
import { fetchWithBaUserToken } from '../../../../shared/utils/fetchWithBaUserToken'

type MembershipTier = 'none' | 'monthly' | 'yearly' | 'lifetime'

const props = defineProps<{
  modelValue: boolean
  tier: MembershipTier
  expiresAt: number
  subscriptionNo: string
  locale: string
  marketingOrigin: string
  token: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  refreshMembership: []
}>()

const store = useMockupEditorStore()
const { t, locale: i18nLocale } = useI18n()

const dialogRef = ref<HTMLDialogElement | null>(null)
const titleId = 'editor-subscription-details-title'

const confirmOpen = ref(false)

const subscriptionNo = computed(() => String(props.subscriptionNo || '').trim())
const hasAuth = computed(() => Boolean(String(props.token || '').trim()))
const isLifetime = computed(() => props.tier === 'lifetime' || props.expiresAt === 0)

const copy = computed(() => ({
  title: t('auth.profile.detailsTitle'),
  close: t('auth.profile.close'),
  subscriptionSectionTitle: t('auth.profile.subscriptionSectionTitle'),
  ordersSectionTitle: t('auth.profile.ordersSectionTitle'),
  currentTier: t('auth.profile.currentTier'),
  expiresLabel: t('auth.profile.expiresLabel'),
  currentPeriod: t('auth.profile.currentPeriod'),
  nextBilling: t('auth.profile.nextBilling'),
  renewal: t('auth.profile.renewal'),
  cancelAtPeriodEnd: t('auth.profile.cancelAtPeriodEnd'),
  loading: t('auth.profile.loading'),
  totalTpl: (n: number) => t('auth.profile.totalTpl', { n }),
  emptyOrders: t('auth.profile.emptyOrders'),
  prevPage: t('auth.profile.prevPage'),
  nextPage: t('auth.profile.nextPage'),
  pageTpl: (p: number) => t('auth.profile.pageTpl', { p }),
  pagerAria: t('auth.profile.pagerAria'),
  orderNoLabel: t('auth.profile.orderNoLabel'),
  payChannelLabel: t('auth.profile.payChannelLabel'),
  payTimeLabel: t('auth.profile.payTimeLabel'),
  productLabel: t('auth.profile.productLabel'),
  unknown: t('auth.profile.unknown'),
  cancelSubscription: t('auth.profile.cancelSubscription'),
  cancelling: t('auth.profile.cancelling'),
  cancelConfirmTitle: t('auth.profile.cancelConfirmTitle'),
  cancelConfirmBody: t('auth.profile.cancelConfirmBody'),
  confirm: t('auth.profile.confirm'),
  cancel: t('auth.profile.cancel'),
  permanent: t('auth.profile.permanent'),
  cancelSuccess: t('auth.profile.cancelSuccess'),
  requestFailed: t('auth.requestFailed'),
  queryUnavailable: t('auth.profile.queryUnavailable'),
  refresh: t('auth.profile.refresh'),
  subStatusPending: t('auth.profile.subStatus.pending'),
  subStatusUnknown: t('auth.profile.subStatus.unknown'),
}))

const proxyJsonErrors = computed<MarketingProxyJsonErrorCopy>(() => ({
  nonJson: t('auth.profile.proxyNonJson'),
  html404: t('auth.profile.proxyHtml404'),
  httpError: (status: number) => t('auth.profile.proxyHttpError', { status }),
}))

function normalizeOrigin(): string {
  return props.marketingOrigin.replace(/\/$/, '')
}

async function requestJson<T>(args: {
  method: 'GET' | 'POST'
  path: string
  query?: Record<string, string | number | boolean | undefined>
  body?: Record<string, unknown>
}): Promise<T> {
  const base = normalizeOrigin()
  let url = `${base}${args.path}`
  if (args.query) {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(args.query)) {
      if (v === undefined) continue
      q.set(k, String(v))
    }
    const s = q.toString()
    if (s) url += `?${s}`
  }
  const init: RequestInit = { method: args.method, credentials: 'include' }
  if (args.method === 'POST') {
    init.headers = { 'content-type': 'application/json' }
    init.body = JSON.stringify(args.body ?? {})
  }
  const res = await fetchWithBaUserToken(url, init, props.token)
  return readJsonFromMarketingProxyResponse<T>(res, proxyJsonErrors.value)
}

function toastError(msg: string): void {
  store.showEditorLayerToast({
    title: t('auth.profile.title'),
    body: msg || copy.value.requestFailed,
    variant: 'error',
    durationMs: 3200,
  })
}

const {
  sub,
  loadingSub,
  subQueryFailed,
  loadingOrders,
  orders,
  page,
  total,
  canceling,
  pageCount,
  pageItems,
  loadSubscription,
  loadOrders,
  goPage,
  cancelAtPeriodEnd,
} = useSubscriptionDetailApi({
  subscriptionNo,
  hasAuth,
  requestJson,
  onError: (message: string) => toastError(message || copy.value.requestFailed),
  refreshMembership: async () => {
    emit('refreshMembership')
  },
})

function orderTypeLabel(orderType: string): string {
  if (orderType === 'one_time') return t('auth.profile.orderType.one_time')
  if (orderType === 'subscription') return t('auth.profile.orderType.subscription')
  return orderType || copy.value.unknown
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function formatDateTimeSeconds(tsSeconds: number): string {
  if (!tsSeconds) return '—'
  try {
    const d = new Date(tsSeconds * 1000)
    if (Number.isNaN(d.getTime())) return '—'
    const y = d.getFullYear()
    const m = pad2(d.getMonth() + 1)
    const day = pad2(d.getDate())
    const hh = pad2(d.getHours())
    const mm = pad2(d.getMinutes())
    const ss = pad2(d.getSeconds())
    return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
  } catch {
    return '—'
  }
}

function channelLabel(ch: unknown): string {
  const s = String(ch || '').trim()
  if (!s) return copy.value.unknown
  return s.toUpperCase()
}

const tierLabel = computed(() => {
  switch (props.tier) {
    case 'monthly':
      return t('auth.profile.tier.monthly')
    case 'yearly':
      return t('auth.profile.tier.yearly')
    case 'lifetime':
      return t('auth.profile.tier.lifetime')
    default:
      return t('auth.profile.tier.none')
  }
})

const expiresText = computed(() => {
  if (isLifetime.value) return copy.value.permanent
  if (!props.expiresAt) return '—'
  return formatDate(props.expiresAt)
})

const isCancelAtPeriodEnd = computed(() => !!sub.value?.cancel_at_period_end)

const showNextBilling = computed(() => {
  return !isLifetime.value && !isCancelAtPeriodEnd.value && !!sub.value?.next_billing_at
})

const showExpires = computed(() => {
  if (isLifetime.value) return true
  if (isCancelAtPeriodEnd.value) return true
  return !subscriptionNo.value
})

/**
 * 关闭订阅入口的展示策略：
 *
 * 主路径：`subscription/query` 已成功且 `sub.status ∈ {active, trialing, past_due}` → 入口可见；
 *
 * 兜底路径：`subscription/query` 暂时不可用（`subQueryFailed=true`）但 props.tier 仍是付费档位
 * 且 `subscriptionNo` 非空 → 入口仍可见。后端 `Subscription::cancel` 内有 `user_id` 归属校验，
 * 即使前端误开启入口也不会越权操作。
 */
const canCancelSubscription = computed(() => {
  if (loadingSub.value) return false
  const st = String(sub.value?.status || '').toLowerCase()
  if (st === 'active' || st === 'trialing' || st === 'past_due') return true
  if (!subQueryFailed.value) return false
  const tier = String(props.tier || 'none')
  if (tier !== 'monthly' && tier !== 'yearly') return false
  return subscriptionNo.value !== ''
})

const subStatusText = computed(() => {
  const s = sub.value?.status
  if (!s) return ''
  return t(`auth.profile.subStatus.${s}` as any)
})

const showSubPlaceholder = computed(
  () => !loadingSub.value && subQueryFailed.value && !sub.value,
)

async function onRetryLoadSubscription(): Promise<void> {
  await loadSubscription(true)
}

watch(
  () => props.modelValue,
  (opened) => {
    if (import.meta.env.SSR) return
    if (!opened) return
    Promise.all([loadSubscription(false), loadOrders(1)])
      .then(() => {
        nextTick(() => dialogRef.value?.querySelector<HTMLElement>('.editor-subd-close')?.focus())
      })
      .catch(() => undefined)
  },
)

onMounted(() => globalThis.addEventListener('keydown', onEscClose))
onBeforeUnmount(() => globalThis.removeEventListener('keydown', onEscClose))

function onEscClose(ev: KeyboardEvent): void {
  if (!props.modelValue || ev.key !== 'Escape') return
  emit('update:modelValue', false)
}

function onClose(): void {
  emit('update:modelValue', false)
}

function toastOk(msg: string): void {
  store.showEditorLayerToast({
    title: t('auth.profile.title'),
    body: msg,
    variant: 'info',
    durationMs: 2800,
  })
}

function formatDate(tsSeconds: number): string {
  try {
    const d = new Date(tsSeconds * 1000)
    if (Number.isNaN(d.getTime())) return '—'
    return new Intl.DateTimeFormat(i18nLocale.value, { dateStyle: 'medium' }).format(d)
  } catch {
    return '—'
  }
}

function formatMoney(amountMinor: number, currency: string): string {
  const safeCurrency = String(currency || '').toUpperCase() || 'USD'
  const v = Number(amountMinor || 0) / 100
  try {
    return new Intl.NumberFormat(i18nLocale.value, { style: 'currency', currency: safeCurrency }).format(v)
  } catch {
    return `${v.toFixed(2)} ${safeCurrency}`
  }
}

async function onCancelAtPeriodEnd(): Promise<void> {
  await cancelAtPeriodEnd(async () => {
    confirmOpen.value = false
    toastOk(copy.value.cancelSuccess)
  })
}

watch(
  () => confirmOpen.value,
  (opened) => {
    if (import.meta.env.SSR || !opened) return
    nextTick(() => {
      globalThis.document.querySelector<HTMLElement>('.editor-subd-confirm-cancel')?.focus()
    })
  },
)
</script>

<style scoped>
.editor-subd-mask,
.editor-subd-confirm-mask {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgb(2 6 23 / 66%);
  padding: 1rem;
}

.editor-subd-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.editor-subd-panel {
  width: min(920px, calc(100vw - 2rem));
  max-height: calc(100dvh - 2rem);
  overflow: auto;
  margin: 0;
  position: relative;
  inset: auto;
  padding: 1rem 1rem 1.1rem;
  border-radius: 14px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  box-shadow: 0 18px 60px rgb(15 23 42 / 20%);
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, #1f7ab8 35%, transparent) transparent;
}

.editor-subd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.editor-subd-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 900;
  color: var(--im-text);
}

.editor-subd-close {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: transparent;
  cursor: pointer;
}

.editor-subd-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-subd-body {
  margin-top: 0.75rem;
  display: grid;
  gap: 0.85rem;
  max-height: 80dvh;
}

.editor-subd-card {
  border-radius: 14px;
  border: 1px solid var(--im-border);
  padding: 0.9rem;
  background: color-mix(in srgb, var(--im-surface) 92%, #0b1220);
}

.editor-subd-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

.editor-subd-card__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 900;
  color: var(--im-text);
}

.editor-subd-chip {
  display: inline-flex;
  align-items: center;
  padding: 0 0.55rem;
  min-height: 28px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, #10b981 38%, transparent);
  font-size: 0.78rem;
  font-weight: 900;
  color: #10b981;
  background: color-mix(in srgb, #10b981 14%, var(--im-surface));
}

.editor-subd-chip--warn {
  border-color: color-mix(in srgb, #f59e0b 50%, transparent);
  background: color-mix(in srgb, #f59e0b 18%, var(--im-surface));
  color: #fbbf24;
}

.editor-subd-placeholder-note {
  margin: 0.45rem 0 0;
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--im-muted, #94a3b8);
  line-height: 1.5;
}

.editor-subd-kv {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.editor-subd-kv__k {
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--im-muted);
}

.editor-subd-kv__v {
  margin-top: 0.15rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: var(--im-text);
}

.editor-subd-actions {
  margin-top: 0.65rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  justify-content: flex-end;
}

.editor-subd-ghost,
.editor-subd-danger {
  min-height: 44px;
  padding: 0 0.85rem;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 800;
  border: 1px solid var(--im-border);
  background: transparent;
  color: var(--im-text);
}

.editor-subd-danger {
  border-color: color-mix(in srgb, #ef4444 55%, var(--im-border));
  color: #ef4444;
}

.editor-subd-danger--soft {
  min-height: 36px;
  padding: 0 0.7rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 850;
  background: transparent;
  opacity: 0.78;
  border-color: color-mix(in srgb, #ef4444 35%, var(--im-border));
  color: color-mix(in srgb, #ef4444 66%, var(--im-text));
}

.editor-subd-danger--soft:hover {
  opacity: 0.92;
  background: rgb(239 68 68 / 6%);
}

.editor-subd-danger--solid {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
}

.editor-subd-muted {
  color: color-mix(in srgb, var(--im-text) 72%, transparent);
  font-weight: 700;
  font-size: 0.82rem;
}

.editor-subd-loading {
  display: grid;
  place-items: center;
  padding: 1.2rem 0.6rem;
  gap: 0.55rem;
}

.editor-subd-spinner {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 3px solid color-mix(in srgb, #1f7ab8 25%, transparent);
  border-top-color: #1f7ab8;
  animation: editor-subd-spin 0.85s linear infinite;
}

@keyframes editor-subd-spin {
  to {
    transform: rotate(360deg);
  }
}

.editor-subd-orders {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.55rem;
}

.editor-subd-order {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
}

.editor-subd-order__subject {
  font-weight: 900;
  color: var(--im-text);
  font-size: 0.92rem;
  line-height: 1.25;
}

.editor-subd-order__plan {
  color: color-mix(in srgb, var(--im-text) 65%, transparent);
  font-weight: 850;
}

.editor-subd-order__grid {
  margin-top: 0.45rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem 0.75rem;
}

.editor-subd-order__kv {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.4rem;
  align-items: baseline;
}

.editor-subd-order__k {
  font-size: 0.74rem;
  font-weight: 900;
  color: var(--im-muted);
  white-space: nowrap;
}

.editor-subd-order__v {
  font-size: 0.82rem;
  font-weight: 850;
  color: var(--im-text);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editor-subd-order__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  letter-spacing: 0.2px;
}

.editor-subd-panel::-webkit-scrollbar {
  width: 10px;
}

.editor-subd-panel::-webkit-scrollbar-track {
  background: transparent;
}

.editor-subd-panel::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, #1f7ab8 28%, transparent);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.editor-subd-panel::-webkit-scrollbar-thumb:hover {
  background: color-mix(in srgb, #1f7ab8 40%, transparent);
  background-clip: padding-box;
}

@media (max-width: 520px) {
  .editor-subd-order__grid {
    grid-template-columns: 1fr;
  }
}

.editor-subd-pill {
  display: inline-flex;
  align-items: center;
  padding: 0 0.45rem;
  min-height: 24px;
  border-radius: 999px;
  border: 1px solid var(--im-border);
  font-size: 0.74rem;
  font-weight: 800;
  color: var(--im-text);
  background: transparent;
}

.editor-subd-order__amount {
  font-weight: 900;
  font-size: 0.95rem;
  color: var(--im-text);
  white-space: nowrap;
}

.editor-subd-empty {
  padding: 1rem 0.6rem;
  text-align: center;
  color: var(--im-muted);
  font-weight: 700;
}

.editor-subd-pager {
  margin-top: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.editor-subd-pagebtn,
.editor-subd-page {
  min-width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.editor-subd-page:disabled,
.editor-subd-pagebtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.editor-subd-page.is-active {
  border-color: color-mix(in srgb, #1f7ab8 55%, var(--im-border));
  background: color-mix(in srgb, #1f7ab8 14%, transparent);
}

.editor-subd-page:focus-visible,
.editor-subd-pagebtn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-subd-confirm-mask {
  display: grid;
  place-items: center;
  z-index: 1200;
}

.editor-subd-confirm {
  width: min(520px, calc(100vw - 2rem));
  border-radius: 14px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  box-shadow: 0 18px 60px rgb(15 23 42 / 20%);
  padding: 0.95rem;
}

.editor-subd-confirm__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 900;
  color: var(--im-text);
}

.editor-subd-confirm__body {
  margin: 0.6rem 0 0;
  color: var(--im-muted);
  font-weight: 650;
  line-height: 1.6;
}

.editor-subd-confirm__actions {
  margin-top: 0.9rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.55rem;
}

.editor-subd-confirm-cancel,
.editor-subd-confirm-ok {
  min-height: 40px;
  padding: 0 0.35rem;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 900;
  border: 1px solid transparent;
  background: transparent;
}

.editor-subd-confirm-cancel {
  color: #1f7ab8;
  font-weight: 900;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.editor-subd-confirm-cancel:hover {
  color: color-mix(in srgb, #1f7ab8 76%, var(--im-text));
}

.editor-subd-confirm-cancel:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgb(59 130 246 / 35%);
}

.editor-subd-confirm-ok {
  color: color-mix(in srgb, #ef4444 62%, var(--im-text));
  font-weight: 800;
  opacity: 0.78;
}

.editor-subd-confirm-ok:hover {
  opacity: 0.92;
  background: rgb(2 6 23 / 6%);
}

@media (max-width: 520px) {
  .editor-subd-kv {
    grid-template-columns: 1fr;
  }
}

.editor-subd-fade-enter-active,
.editor-subd-fade-leave-active {
  transition: opacity 180ms ease;
}
.editor-subd-fade-enter-from,
.editor-subd-fade-leave-to {
  opacity: 0;
}

.editor-subd-pop-enter-active {
  transition:
    transform 200ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 200ms ease;
}
.editor-subd-pop-enter-from {
  transform: translateY(10px) scale(0.985);
  opacity: 0;
}
.editor-subd-pop-leave-active {
  transition:
    transform 160ms ease,
    opacity 160ms ease;
}
.editor-subd-pop-leave-to {
  transform: translateY(8px) scale(0.99);
  opacity: 0;
}
</style>

